import type { Cell } from './camera';

/** How far (in lots) a house counts as near the one just tended. */
export const NEAR_LOTS = 6;
/** At most this many nearby houses glint at the end of a tended house. */
export const NEARBY_MAX = 4;

type Candidates = { due: readonly string[]; dark: readonly string[] };
const near = (a: Cell, b: Cell) => Math.hypot(b[0] - a[0], b[1] - a[1]) <= NEAR_LOTS;

/** Oldest overdue nearby (six lots), then oldest overdue elsewhere; dark houses follow overdue ones. */
export function nextTend(
  candidates: Candidates,
  current: string,
  visited: ReadonlySet<string>,
  cellOf: (key: string) => Cell | null,
): string | null {
  const from = cellOf(current);
  for (const group of [candidates.due, candidates.dark]) {
    const keys = group.filter((key) => key !== current && !visited.has(key));
    const nearby = from && keys.find((key) => {
      const c = cellOf(key);
      return c && near(from, c);
    });
    if (nearby) return nearby;
    if (keys.length) return keys[0];
  }
  return null;
}

/**
 * The houses that glint around the one just tended: within six lots of it, overdue (oldest first) before dark, the
 * current house and every house visited in this walk left out, at most four.
 */
export function nearbyTends(
  candidates: Candidates,
  current: string,
  visited: ReadonlySet<string>,
  cellOf: (key: string) => Cell | null,
): { key: string; kind: 'due' | 'dark' }[] {
  const from = cellOf(current);
  if (!from) return [];
  const out: { key: string; kind: 'due' | 'dark' }[] = [];
  for (const [kind, group] of [['due', candidates.due], ['dark', candidates.dark]] as const) {
    for (const key of group) {
      if (out.length >= NEARBY_MAX) return out;
      if (key === current || visited.has(key) || out.some((o) => o.key === key)) continue;
      const c = cellOf(key);
      if (c && near(from, c)) out.push({ key, kind });
    }
  }
  return out;
}
